import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import {
    CardFieldMode,
    CreateBusinessCardFromTemplateRequest,
    DigitalBusinessCardInputModel,
    EffectiveBusinessCardConfig,
    OrgBusinessCardFieldKey,
    OrgBusinessCardFieldRule,
    OrgBusinessCardVisibleTabs
} from '../model/org-business-card.model';

const DEFAULT_RULES: Record<OrgBusinessCardFieldKey, OrgBusinessCardFieldRule> = {
    logo: { mode: 'locked', required: true },
    photo: { mode: 'editable', required: true },
    firstName: { mode: 'editable', required: true },
    lastName: { mode: 'editable', required: true },
    userName: { mode: 'editable', required: true },
    email: { mode: 'editable', required: true },
    organization: { mode: 'locked', required: true },
    title: { mode: 'editable', required: false },
    description: { mode: 'editable', required: false },
    phone: { mode: 'editable', required: false },
    links: { mode: 'editable', required: false },
    social: { mode: 'editable', required: false },
    products: { mode: 'hidden', required: false },
    customForm: { mode: 'hidden', required: false },
    template: { mode: 'locked', required: true },
    colors: { mode: 'locked', required: true }
};

const FIELD_CONTROL_MAP: Record<OrgBusinessCardFieldKey, string[]> = {
    logo: ['logo'],
    photo: ['photo'],
    firstName: ['firstName'],
    lastName: ['lastName'],
    userName: ['userName'],
    email: ['email'],
    organization: ['organization'],
    title: ['title'],
    description: ['description'],
    phone: ['phone'],
    links: ['links'],
    social: ['social'],
    products: ['productCarousel'],
    customForm: ['customForm'],
    template: ['template'],
    colors: [
        'primaryColor',
        'secondaryColor',
        'digitalCardTextColor',
        'walletTextColor',
        'walletBackgroundColor',
        'backgroundImage',
        'backgroundImageOpacity',
        'appleWalletLogo',
        'poweredBy',
        'poweredByUrl',
        'includePoweredByUrl',
        'brandedQr',
        'customizeWallet'
    ]
};

export function getFieldRule(fieldName: string, rules: Record<string, OrgBusinessCardFieldRule> | null | undefined): OrgBusinessCardFieldRule {
    if (!rules) {
        return { mode: 'editable', required: false };
    }

    return rules[fieldName] ?? DEFAULT_RULES[fieldName as OrgBusinessCardFieldKey] ?? { mode: 'editable', required: false };
}

export function isFieldHidden(fieldName: string, rules: Record<string, OrgBusinessCardFieldRule> | null | undefined): boolean {
    return getFieldRule(fieldName, rules).mode === 'hidden';
}

export function isFieldLocked(fieldName: string, rules: Record<string, OrgBusinessCardFieldRule> | null | undefined): boolean {
    return getFieldRule(fieldName, rules).mode === 'locked';
}

export function isFieldRequired(fieldName: string, rules: Record<string, OrgBusinessCardFieldRule> | null | undefined): boolean {
    return getFieldRule(fieldName, rules).required;
}

export function getVisibleTabs(rules: Record<string, OrgBusinessCardFieldRule> | null | undefined): OrgBusinessCardVisibleTabs {
    return {
        phone: !isFieldHidden('phone', rules),
        links: !isFieldHidden('links', rules),
        social: !isFieldHidden('social', rules),
        products: !isFieldHidden('products', rules),
        customForm: !isFieldHidden('customForm', rules),
        template: !isFieldHidden('template', rules),
        colors: !isFieldHidden('colors', rules)
    };
}

export function applyFieldRulesToForm(formGroup: FormGroup, rules: Record<string, OrgBusinessCardFieldRule> | null | undefined): void {
    (Object.keys(FIELD_CONTROL_MAP) as OrgBusinessCardFieldKey[]).forEach((fieldKey: OrgBusinessCardFieldKey) => {
        const controlNames = FIELD_CONTROL_MAP[fieldKey];
        const rule = getFieldRule(fieldKey, rules);

        controlNames.forEach((controlName: string) => {
            const control = formGroup.get(controlName);
            if (!control) {
                return;
            }

            applyMode(control, rule.mode);
            applyRequiredValidator(control, rule.required);
            control.updateValueAndValidity({ emitEvent: false });
        });
    });
}

function applyMode(control: AbstractControl, mode: CardFieldMode): void {
    if (mode === 'locked') {
        control.disable({ emitEvent: false });
        return;
    }

    if (control.disabled) {
        control.enable({ emitEvent: false });
    }
}

function applyRequiredValidator(control: AbstractControl, required: boolean): void {
    if (control instanceof FormArray) {
        control.setValidators(required ? [minLengthArrayValidator(1)] : []);
        return;
    }

    if (control instanceof FormControl) {
        if (required) {
            control.addValidators(Validators.required);
        } else {
            control.removeValidators(Validators.required);
        }
    }
}

export function minLengthArrayValidator(minLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!(control instanceof FormArray)) {
            return null;
        }

        return control.length >= minLength ? null : { requiredArray: true };
    };
}

export function sanitizeCardInputForRules(
    card: DigitalBusinessCardInputModel,
    rules: Record<string, OrgBusinessCardFieldRule> | null | undefined
): DigitalBusinessCardInputModel {
    const sanitizedCard: DigitalBusinessCardInputModel = { ...card };

    (Object.keys(FIELD_CONTROL_MAP) as OrgBusinessCardFieldKey[]).forEach((fieldKey: OrgBusinessCardFieldKey) => {
        if (!isFieldHidden(fieldKey, rules)) {
            return;
        }

        switch (fieldKey) {
            case 'products':
                delete sanitizedCard.productCarousel;
                break;
            case 'customForm':
                delete sanitizedCard.customForm;
                break;
            case 'phone':
            case 'links':
            case 'social':
                delete sanitizedCard[fieldKey];
                break;
            case 'colors':
                delete sanitizedCard.primaryColor;
                delete sanitizedCard.secondaryColor;
                delete sanitizedCard.digitalCardTextColor;
                delete sanitizedCard.walletTextColor;
                delete sanitizedCard.walletBackgroundColor;
                delete sanitizedCard.backgroundImage;
                delete sanitizedCard.backgroundImageOpacity;
                delete sanitizedCard.appleWalletLogo;
                delete sanitizedCard.poweredBy;
                delete sanitizedCard.poweredByUrl;
                delete sanitizedCard.includePoweredByUrl;
                delete sanitizedCard.brandedQr;
                delete sanitizedCard.customizeWallet;
                break;
            default:
                delete sanitizedCard[fieldKey];
                break;
        }
    });

    return sanitizedCard;
}

export function buildCreateFromTemplateRequest(
    orgId: number,
    projectId: string,
    ownerUserId: string,
    teamId: string | null | undefined,
    card: DigitalBusinessCardInputModel,
    effectiveConfig: EffectiveBusinessCardConfig | null
): CreateBusinessCardFromTemplateRequest {
    const rules = effectiveConfig?.fieldRules;

    return {
        orgId,
        projectId,
        ownerUserId,
        teamId: teamId || undefined,
        card: sanitizeCardInputForRules(card, rules)
    };
}
